import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  LEGAL_DOCUMENTS,
  termsNoticePresentationFor,
  type TermsNoticePresentation,
  type TermsNoticeVerdict,
} from '@lds-efsp/shared-contracts';
import { useLegalApi } from '../../api/useLegalApi';
import { NO_LEGAL_NOTICE, useLegalNoticeState } from './useLegalNoticeState';

/**
 * The version whose notice this SESSION has already moved focus to, or null
 * before any notice has claimed it. Module scope is load-bearing rather than a
 * micro-optimisation: `ProtectedRoute` is a per-route element, so walking from
 * one customer route to the next unmounts and remounts the whole notice
 * surface. A `useRef` would reset with the mount and take focus back on every
 * navigation, which is exactly what the requirement forbids — focus is claimed
 * once per announced version and never again while that same notice is up.
 *
 * The record is a version NUMBER, not a boolean: a later material version is a
 * new notice and does claim focus.
 */
let focusedNoticeVersion: number | null = null;

/** The failure copy for a dismissal that failed without an Error to read. */
const DISMISSAL_FAILED = 'Failed to record notice dismissal';

export interface TermsNoticeControls {
  /** The notice to render: the read's verdict, quieted once dismissed. */
  verdict: TermsNoticeVerdict;
  /**
   * Where the notice's link leads and what it may say, from the announced
   * version's standing at the instant this mount pinned. Answered by the
   * shared decider rather than derived here, so the link and the sentence
   * cannot describe different versions.
   */
  presentation: TermsNoticePresentation;
  /**
   * Record the dismissal of `version` with the server. Never throws and never
   * navigates — a refusal leaves the notice standing so the customer can read
   * the server's own sentence and try again.
   */
  dismiss: (version: number) => Promise<void>;
  /** The server's own message from the last failed dismissal, null when none. */
  dismissalError: string | null;
  /** The always-mounted container the notice renders into and focuses on arrival. */
  containerRef: RefObject<HTMLDivElement | null>;
}

/**
 * The verdict as a total value. {@link useLegalNoticeState} declares a verdict
 * and the acceptance-state schema declares `notice` a required leg with
 * explicit nulls — but the read behind it is an unvalidated typed fetch
 * (`useApi.get` returns `response.json()` with no parse), so a body that omits
 * the leg entirely reaches the reconciliation fold as `undefined` and throws
 * on `reported.due` at `useLegalNoticeState.ts:82`. That throw happens inside
 * the read's `.then`, so the read's own `.catch` at `:182` already answers it
 * with the quiet verdict — the fail-open path, whose whole cost is one lost
 * read and a 30 s retry, never a broken route. What reaches this guard
 * unnormalised is the malformed-but-PRESENT case: an object such as `{}`, or
 * any object that does not say `due === true`, which the fold's `local.due ?
 * local : reported` hands through as its own answer whenever the bundled half
 * is quiet. The guard normalises that at the surface boundary. A read that did
 * not say `due` is a read that announced nothing: answer it with the module's
 * own quiet constant, the same answer every other unreadable state gets.
 */
export function noticeVerdictOrNone(
  value: TermsNoticeVerdict | undefined | null,
): TermsNoticeVerdict {
  return value && value.due === true ? value : NO_LEGAL_NOTICE;
}

/**
 * The notice surface's state: the read's verdict composed with this session's
 * dismissal, and the container the notice renders into.
 *
 * It composes {@link useLegalNoticeState} and the dismissal write rather than
 * widening either: the read module still answers exactly one verdict and reads
 * no clock of its own, and the API module still owns the endpoint. Nothing
 * here re-derives the verdict — the quieting below is a session-local
 * standing over an answer already made, never a second evaluation of the
 * registry.
 *
 * Deliberate behaviors:
 * - One action. `dismiss` is the only thing the surface offers and the only
 *   write it makes; there is no accept, no acknowledgement to record, and
 *   nothing a customer must do to keep using the application.
 * - A dismissal is quieting, not locking. On success the notice empties and
 *   the verdict reads no-notice-due for the rest of the session — from this
 *   session's own standing immediately, and from the read's recorded
 *   dismissal on every route change after it (the shared predicate reads that
 *   standing itself, so the quiet survives the remount).
 * - A failed dismissal is visible, not silent, and refuses no route: the
 *   notice stays up with the server's own message inside it.
 * - Focus is claimed once per announced version (see
 *   {@link focusedNoticeVersion}) and is never trapped — the container takes
 *   focus and the next Tab leaves it.
 * - The presentation the surface renders arrives from the shared decider, read
 *   at the one instant this mount pinned. Asking the registry a question is not
 *   re-deriving its answer: which version is announced stays
 *   `termsNoticeVerdict`'s own answer, and only what that answer looks like on
 *   the surface is decided here.
 */
export function useTermsNotice(): TermsNoticeControls {
  const { verdict: readVerdict } = useLegalNoticeState();
  const { dismissTermsNotice } = useLegalApi();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dismissedVersion, setDismissedVersion] = useState<number | null>(null);
  const [dismissalError, setDismissalError] = useState<string | null>(null);
  // The instant the registry is read at, pinned ONCE per mount in a state
  // initializer rather than re-read on every render: the announced version's
  // link target and its wording have to describe the same standing, and a
  // clock read per render could flip the pair out from under a notice that
  // arrived on an earlier one.
  const [now] = useState(() => new Date().toISOString());

  const verdict = noticeVerdictOrNone(readVerdict);

  // A dismissal answers the announced version and everything BELOW it — never
  // that version alone. A later, higher-numbered material version is a fresh
  // announcement and must be noticed again.
  const quieted =
    verdict.due &&
    verdict.version !== null &&
    dismissedVersion !== null &&
    verdict.version <= dismissedVersion;
  const effective = quieted ? NO_LEGAL_NOTICE : verdict;

  // The announced version's standing at the pinned instant, read through the
  // one shared decider. Nothing here re-derives it: the registry's own
  // selectors answer which version is announced, and this only asks what that
  // answer looks like on the surface.
  const presentation = termsNoticePresentationFor(LEGAL_DOCUMENTS, effective.version, now);

  const dismiss = useCallback(
    async (version: number): Promise<void> => {
      try {
        await dismissTermsNotice(version);
        setDismissedVersion(version);
        setDismissalError(null);
      } catch (error) {
        setDismissalError(error instanceof Error ? error.message : DISMISSAL_FAILED);
      }
    },
    [dismissTermsNotice],
  );

  useEffect(() => {
    if (!effective.due || effective.version === null) return;
    if (focusedNoticeVersion === effective.version) return;
    focusedNoticeVersion = effective.version;
    containerRef.current?.focus();
  }, [effective.due, effective.version]);

  return { verdict: effective, presentation, dismiss, dismissalError, containerRef };
}
